import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ShieldCheck, Send, CheckCircle2, Info } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export default function OfficialsApply() {
  const [sp] = useSearchParams();
  const prefillType = sp.get("type") || sp.get("entity_type") || "";
  const prefillId = sp.get("id") || sp.get("entity_id") || "";
  const prefillName = sp.get("name") || sp.get("entity_name") || "";
  const [form, setForm] = useState({
    entity_type: prefillType, entity_id: prefillId, entity_name: prefillName,
    org_name: "", role_title: "", work_email: "", proof_note: "",
  });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const hasEntity = !!(form.entity_type && form.entity_id && form.entity_name);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/officials/request", form); setSent(true); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not submit request"); }
    setBusy(false);
  };

  return (
    <div className="max-w-2xl mx-auto px-5 lg:px-8 py-14" data-testid="officials-apply">
      <div className="text-xs font-mono uppercase tracking-widest text-sky-600 mb-2 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Official Accounts</div>
      <h1 className="font-heading font-900 text-4xl sm:text-5xl tracking-tight">Represent your airline or airport</h1>
      <p className="mt-3 text-muted-foreground">Request a verified official badge so you can respond publicly to travellers on your organisation's page. Our team reviews each request before approval.</p>

      {sent ? (
        <div className="mt-10 border border-emerald-300 bg-emerald-50/50 p-8 text-center" data-testid="officials-apply-success">
          <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
          <h2 className="font-heading font-900 text-xl">Request submitted</h2>
          <p className="text-muted-foreground mt-1">Thanks — we'll review your request and email you once your official access is approved.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-4">
          {!hasEntity && (
            <div className="border border-amber-200 bg-amber-50/50 p-3 text-sm text-amber-800 flex items-start gap-2">
              <Info className="w-4 h-4 shrink-0 mt-0.5" /> Tip: open your airline or airport page and click "Represent this page" to prefill it automatically.
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block"><span className="text-sm font-600">Page type</span>
              <select value={form.entity_type} onChange={(e) => setForm({ ...form, entity_type: e.target.value })} required data-testid="officials-type" className="mt-1 w-full border border-zinc-300 px-3 py-2.5 bg-white outline-none focus:ring-2 focus:ring-primary">
                <option value="">Select…</option><option value="airline">Airline</option><option value="airport">Airport</option>
              </select></label>
            <label className="block"><span className="text-sm font-600">Airline / airport name</span>
              <input value={form.entity_name} onChange={(e) => setForm({ ...form, entity_name: e.target.value })} required data-testid="officials-entity-name" className="mt-1 w-full border border-zinc-300 px-3 py-2.5 outline-none focus:ring-2 focus:ring-primary" /></label>
          </div>
          <input value={form.entity_id} onChange={(e) => setForm({ ...form, entity_id: e.target.value })} placeholder="Page ID (auto-filled from the page)" data-testid="officials-entity-id" className="w-full border border-zinc-300 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary" required />
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block"><span className="text-sm font-600">Organisation / team</span>
              <input value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value })} required data-testid="officials-org" placeholder="e.g. United Airlines PR" className="mt-1 w-full border border-zinc-300 px-3 py-2.5 outline-none focus:ring-2 focus:ring-primary" /></label>
            <label className="block"><span className="text-sm font-600">Your role</span>
              <input value={form.role_title} onChange={(e) => setForm({ ...form, role_title: e.target.value })} required data-testid="officials-role" placeholder="e.g. Social Media Manager" className="mt-1 w-full border border-zinc-300 px-3 py-2.5 outline-none focus:ring-2 focus:ring-primary" /></label>
          </div>
          <label className="block"><span className="text-sm font-600">Work email (company domain)</span>
            <input type="email" value={form.work_email} onChange={(e) => setForm({ ...form, work_email: e.target.value })} required data-testid="officials-work-email" className="mt-1 w-full border border-zinc-300 px-3 py-2.5 outline-none focus:ring-2 focus:ring-primary" /></label>
          <label className="block"><span className="text-sm font-600">How can we verify you? (LinkedIn, press page, etc.)</span>
            <textarea value={form.proof_note} onChange={(e) => setForm({ ...form, proof_note: e.target.value })} rows={4} data-testid="officials-proof" className="mt-1 w-full border border-zinc-300 px-3 py-2.5 outline-none focus:ring-2 focus:ring-primary" /></label>
          <button type="submit" disabled={busy} data-testid="officials-submit" className="inline-flex items-center gap-2 bg-sky-600 text-white px-6 py-3 font-600 hover:bg-sky-700 disabled:opacity-60 transition-colors">
            <Send className="w-4 h-4" /> {busy ? "Submitting…" : "Submit for verification"}
          </button>
        </form>
      )}
    </div>
  );
}
